import { Fragment, useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import Avatar from "@/design/components/Avatar";
import FormGroup from "@/design/forms/FormGroup";
import { SaveState } from "@/utils/hooks/useSave";
import Valkyrie, {
  viArrowDownShortWide,
  viArrowLeft,
  viFilmstrip,
  viGear,
  viListChecks,
  viMusic,
} from "@sippy-platform/valkyrie";

import { move } from "@dnd-kit/helpers";
import { DragDropProvider } from "@dnd-kit/react";
import {
  Alert,
  AlertTitle,
  AppBar,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  Grid,
  IconButton,
  List,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { amber, blue, lightGreen, purple, red, teal } from "@mui/material/colors";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import PropTypes from "prop-types";

import CodecItem from "./_CodecItem";
import NavigationItem from "./NavigationItem";

function CodecManager({ user, setSaveState, dispatch }) {
  const theme = useTheme();
  const matchedMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const codecStore = useSelector((state) => state.system.codecs);

  const initialUserCodecList =
    user.codecs
      ?.map((codec) => codecStore.list.find((_codec) => _codec.codec === codec))
      .filter((item) => item !== undefined) || [];
  const codecListKey = `${user.id}:${user.codecs?.join(",")}:${codecStore.list.map((codec) => codec.codec).join(",")}`;
  const [userCodecList, setUserCodecList] = useState(initialUserCodecList);
  const [previousCodecListKey, setPreviousCodecListKey] = useState(codecListKey);
  const [codecPage, setCodecPage] = useState(false);

  if (codecListKey !== previousCodecListKey) {
    setPreviousCodecListKey(codecListKey);
    setUserCodecList(initialUserCodecList);
  }

  const manageCodecList = useMemo(() => {
    let recommend = user.softphone === 1 ? ["alaw", "opus"] : ["alaw", "g722", "h263", "h264"];

    return {
      Recommended: codecStore.list.filter((codec) => recommend.indexOf(codec.codec) !== -1),
      Other: codecStore.list.filter((codec) => recommend.indexOf(codec.codec) === -1),
    };
  }, [user.softphone, codecStore.list]);

  const onCodecToggle = (codec) => {
    // Manage codec list
    let _userCodecList = Object.assign([], userCodecList);

    if (_userCodecList.map((_codec) => _codec.codec).indexOf(codec.codec) >= 0) {
      _userCodecList = _userCodecList.filter((_codec) => _codec.codec !== codec.codec);
    } else {
      if (codec.type === "video") {
        const firstAudioCodec = _userCodecList.map((_codec) => _codec.type).indexOf("audio");

        if (firstAudioCodec === -1) {
          _userCodecList = [..._userCodecList, codec];
        } else {
          _userCodecList.splice(firstAudioCodec, 0, codec);
        }
      } else {
        _userCodecList = [..._userCodecList, codec];
      }
    }

    setUserCodecList(_userCodecList);

    // Manage user
    const hasVideoCodec = _userCodecList.filter((_codec) => _codec.type === "video");

    dispatch({
      type: "codecs",
      value: _userCodecList.map((codec) => codec.codec),
      videosupport: hasVideoCodec.length ? "yes" : "no",
    });
    setSaveState(SaveState.UNSAVED);
  };

  // Show video after audio warning
  const showVideoAfterAudioWarning = useMemo(() => {
    const indexOfFirstAudio = userCodecList.map((_codec) => _codec.type).indexOf("audio");
    const indexOfLastVideo = userCodecList.map((_codec) => _codec.type).lastIndexOf("video");

    return indexOfLastVideo > indexOfFirstAudio;
  }, [userCodecList]);

  const getCodecColor = useCallback((codec) => {
    if (codec.startsWith("opus")) return blue[500];
    if (codec.startsWith("h2")) return red[500];
    if (codec.startsWith("g7") || codec.startsWith("alaw") || codec.startsWith("ulaw")) return lightGreen[500];
    if (codec.startsWith("siren")) return teal[500];
    if (codec.startsWith("vp")) return amber[500];
    return purple[500];
  }, []);

  return (
    <FormGroup
      title="Codecs"
      actions={
        <Button
          variant="contained"
          onClick={() => setCodecPage("manage")}
          startIcon={<Valkyrie icon={viGear} />}
          id="manageCodecs"
        >
          Manage codecs
        </Button>
      }
    >
      <Grid size={12}>
        {userCodecList.length >= 1 ? (
          userCodecList.map((codec) => (
            <Chip
              key={codec.codec}
              label={codec.codec}
              avatar={
                <Avatar icon={codec.type === "video" ? viFilmstrip : viMusic} color={getCodecColor(codec.codec)} />
              }
            />
          ))
        ) : (
          <Alert severity="error">
            <AlertTitle>No codec set</AlertTitle>
            <Typography>
              You don't have any codecs set to make calls with. You may not be able to make any calls.
            </Typography>
          </Alert>
        )}
        <Dialog
          open={!!codecPage}
          onClose={() => setCodecPage(false)}
          aria-labelledby="scodec-manager-dialog"
          maxWidth="lg"
          slotProps={{
            paper: {
              elevation: 1,
              id: "codecManager",
              sx: {
                p: 0,
                width: "100% !important",
                display: "flex",
                flexFlow: "row nowrap",
                flexGrow: 1,
                maxHeight: { sx: "100%", md: 800 },
                height: { sx: 1, md: "calc(100% - 64px)" },
                m: { sx: 0, md: 4 },
                contain: "paint",
                [theme.breakpoints.down("md")]: {
                  borderRadius: 0,
                  height: "100%",
                  maxHeight: "100%",
                  margin: 0,
                },
              },
            },
          }}
          fullScreen={!matchedMdUp}
        >
          <Box
            sx={{
              width: { xs: 56, md: 260 },
              flexShrink: 0,
              maxHeight: "100%",
              display: "flex",
              flexFlow: "column nowrap",
              borderRight: "solid 1px",
              borderColor: (theme) => theme.vars.palette.divider,
              height: 1,
              overflowY: "auto",
              position: "relative",
            }}
          >
            <AppBar
              sx={(theme) => ({
                backgroundImage: "none",
                backgroundColor: `color-mix(in srgb, ${theme.vars.palette.background.default} 75%, transparent)`,
                backdropFilter: "blur(15px) saturate(125%)",
                position: "sticky",
                top: 0,
                color: "inherit",
                border: "none",
                zIndex: 1200,
              })}
            >
              <Stack
                direction="row"
                sx={{
                  alignItems: "center",
                  gap: 0,
                  m: 1,
                }}
              >
                <IconButton sx={{ height: 40, width: 40 }} color="inherit" onClick={() => setCodecPage(false)}>
                  <Valkyrie icon={viArrowLeft} />
                </IconButton>
                {matchedMdUp && (
                  <Typography variant="h4" sx={{ m: 0, fontSize: 22 }}>
                    Codec manager
                  </Typography>
                )}
              </Stack>
            </AppBar>
            <List
              id="nav-codec-manager"
              sx={{
                flexGrow: 1,
                display: "flex",
                flexFlow: "column nowrap",
                px: 1,
                pb: 1,
                ".MuiListItem-root": {
                  width: 1,
                  minHeight: 40,
                },
                ".MuiListItemButton-root": {
                  width: 1,
                  minHeight: 40,
                  py: 0,
                  pr: 1.5,
                  pl: 1,
                },
                ".MuiListItemText-primary": {
                  fontWeight: "600",
                },
              }}
            >
              <NavigationItem
                title="Manage"
                icon={viListChecks}
                selected={codecPage === "manage"}
                onClick={() => setCodecPage("manage")}
                id="manageItem"
              />
              <NavigationItem
                title="Priority"
                icon={viArrowDownShortWide}
                selected={codecPage === "priority"}
                onClick={() => setCodecPage("priority")}
                id="priorityItem"
              />
            </List>
          </Box>
          <Box
            sx={{
              flexGrow: 1,
              flexFlow: "column nowrap",
              maxHeight: "100%",
              overflowY: "auto",
              p: 0,
            }}
          >
            {codecPage === "manage" && (
              <>
                <Typography variant="h4" sx={{ px: 2, pt: 1.75, pb: 1.5, fontSize: 22 }}>
                  Manage
                </Typography>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ py: 1, pl: 2, pr: 1.5 }} colSpan={2}>
                        Codec
                      </TableCell>
                      <TableCell sx={{ py: 1, px: 1.5 }}>Description</TableCell>
                      <TableCell sx={{ py: 1, px: 1.5 }}>Experimental</TableCell>
                      <TableCell sx={{ py: 0.5, pl: 1.5, pr: 2, width: 30 }} align="center" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.keys(manageCodecList).map((type) => (
                      <Fragment key={type}>
                        <TableRow>
                          <TableCell colSpan={6} sx={{ p: 2 }}>
                            {type}
                          </TableCell>
                        </TableRow>
                        {manageCodecList[type].map((codec) => {
                          const disabled = userCodecList.length < 20 || userCodecList.indexOf(codec.codec) >= 0;

                          return (
                            <TableRow
                              hover={disabled}
                              onClick={disabled ? () => onCodecToggle(codec) : undefined}
                              key={codec.codec}
                              sx={{ cursor: "pointer" }}
                            >
                              <TableCell sx={{ p: 0, pl: 2, pr: 1.5, width: 24 }}>
                                <Avatar
                                  icon={codec.type === "video" ? viFilmstrip : viMusic}
                                  color={getCodecColor(codec.codec)}
                                  px={24}
                                />
                              </TableCell>
                              <TableCell sx={{ py: 1.25, px: 1.5 }}>{codec.codec}</TableCell>
                              <TableCell sx={{ py: 1.25, px: 1.5 }}>{codec.description}</TableCell>
                              <TableCell sx={{ py: 1.25, px: 1.5, opacity: 0.6 }}>
                                {codec.experimental === "yes" ? "Experimental" : ""}
                              </TableCell>
                              <TableCell sx={{ p: 0, pl: 1.5, pr: 2 }}>
                                <Checkbox
                                  size="small"
                                  checked={userCodecList.map((_codec) => _codec.codec).indexOf(codec.codec) >= 0}
                                  disabled={!disabled}
                                  sx={{
                                    p: 0,
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </Fragment>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
            {codecPage === "priority" && (
              <Box sx={{ px: 2, pt: 1.75 }}>
                <Typography variant="h4" sx={{ pb: 1.5, fontSize: 22 }}>
                  Priority
                </Typography>
                {showVideoAfterAudioWarning && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <AlertTitle>There is a video codec prioritized after an audio codec</AlertTitle>
                    <Typography>
                      It is strongly recommended to set the priority of your video codec higher than your audio codec.
                      If your first codec is an audio codec, videocalling will not work.
                    </Typography>
                  </Alert>
                )}
                {userCodecList.length === 0 && (
                  <Alert severity="error">
                    <AlertTitle>No codec set</AlertTitle>
                    <Typography>
                      You don't have any codecs set to make calls with. You may not be able to make any calls.
                    </Typography>
                  </Alert>
                )}
                <Stack
                  sx={{
                    gap: 1,
                  }}
                >
                  <DragDropProvider
                    onDragEnd={(event) => {
                      const _userCodecList = move(userCodecList, event);

                      setUserCodecList(_userCodecList);
                      dispatch({
                        type: "codecs",
                        value: _userCodecList.map((codec) => codec.codec),
                      });
                      setSaveState(SaveState.UNSAVED);
                    }}
                  >
                    {userCodecList.map((_codec, index) => (
                      <CodecItem key={_codec.codec} codec={_codec} id={_codec.id} index={index} />
                    ))}
                  </DragDropProvider>
                </Stack>
              </Box>
            )}
          </Box>
        </Dialog>
      </Grid>
    </FormGroup>
  );
}

CodecManager.propTypes = {
  user: PropTypes.object,
  setSaveState: PropTypes.func,
  dispatch: PropTypes.func,
};

export default CodecManager;
